import { useEffect, useState } from 'react';
import { LoadingOrError } from '../../../components/LoadingOrError';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { getUser, updateUser } from '../../../common/api/user';
import { KeyValuePair } from '../../../components/KeyValuePair';
import { Container } from '../../../components/Container';
import { MainLink } from '../../../components/MainLink';
import { MainButton } from '../../../components/MainButton';
import { User, UserUpdateRequest } from '../../../common/types';
import { MainInput } from '../../../components/MainInput';
import { useNavigate } from 'react-router';
import { ColumnView } from '../../../components/ColumnView';

export default function UserUpdateView() {
  const { isLoaded, isSignedIn, userId } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [user, setUser] = useState<User | null>(null);

  const { isPending, isFetching, error, data, refetch } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => await getUser(),
    staleTime: 0,
    refetchOnWindowFocus: false,
    enabled: isLoaded && isSignedIn,
  });

  const save = useMutation({
    mutationFn: async (req: UserUpdateRequest) => await updateUser(req),
    onSuccess: () => {
      navigate('/user');
    },
  });

  useEffect(() => {
    if (user) {
      setUsername(user.username);
    }
  }, [user]);

  useEffect(() => {
    if (data) {
      setUser(data);
    }
  }, [data]);

  const loading = (isPending || isFetching) && !error;

  const handleSave = async () => {
    const req: UserUpdateRequest = { username: username.trim() };
    await save.mutateAsync(req);
  };

  return (
    <ColumnView columns={2}>
      <div>
        {loading || error ? (
          <LoadingOrError loading={loading} error={error} retry={refetch} />
        ) : user ? (
          <Container>
            <h1 className="text-2xl font-bold mb-4 text-dark">Update Your Account</h1>
            <div className="grid grid-cols-1">
              <KeyValuePair
                title="Username"
                value={
                  <MainInput
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                    }}
                    className="w-full"
                  />
                }
              />
            </div>
            <div className="grid grid-cols-1 gap-4">
              <MainButton
                onClick={handleSave}
                variant="secondary"
                disabled={username === user.username || save.isPending}
              >
                <span className="text-xl font-semibold">Save</span>
              </MainButton>
              <MainLink to="/user" variant="primary">
                <span className="text-xl font-semibold">Cancel</span>
              </MainLink>
              {save.isPending || save.error ? (
                <LoadingOrError loading={save.isPending} error={save.error} retry={handleSave} />
              ) : null}
            </div>
          </Container>
        ) : null}
      </div>
    </ColumnView>
  );
}
